import type { FeatureFlags, WidgetInstance, WidgetType } from './types';

/**
 * The widget catalogue.
 *
 * One description of each widget, read by three consumers:
 *   - the admin, which builds the palette and the inspector forms from it,
 *   - the validator, which checks a config against it before publish,
 *   - the sanitiser, which repairs a config against it at load.
 *
 * Because all three read the same definition, adding a widget is one entry here
 * plus one component per client — there is no separate schema to keep in step,
 * and the admin cannot offer a field the app will not honour.
 */

export type FieldKind =
  | 'text' | 'textarea' | 'number' | 'toggle' | 'select'
  | 'nav' | 'datetime' | 'list' | 'track' | 'day' | 'session';

export interface PropField {
  key: string;
  label: string;
  kind: FieldKind;
  help?: string;
  placeholder?: string;
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  maxLength?: number;
  required?: boolean;
  /** For `list`: 'text' for a list of strings, or the fields of each object. */
  item?: PropField[] | 'text';
  minItems?: number;
  maxItems?: number;
  itemLabel?: string;
  /** Show this field only when a sibling has a given value. */
  showWhen?: { key: string; equals: unknown };
}

export type WidgetCategory = 'Today' | 'Schedule' | 'Engage' | 'Commerce' | 'Content';

export interface WidgetDef {
  type: WidgetType;
  label: string;
  category: WidgetCategory;
  description: string;
  fields: PropField[];
  defaults: Record<string, unknown>;
  /** The feature flag that must be on for this widget to render. */
  requires?: keyof FeatureFlags;
  /** True when this widget reads the attendee's behaviour. */
  behavioural?: boolean;
}

const ART_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'photo-ops', label: 'Photo studio' },
  { value: 'special-events', label: 'Stage' },
  { value: 'exhibitor-hall', label: 'Exhibitor hall' },
  { value: 'venue-map', label: 'Venue map' },
  { value: 'schedule', label: 'Schedule grid' },
  { value: 'talent', label: 'Portraits' },
  { value: 'quests', label: 'Badge' },
];

export const NAV_SCREENS = [
  { value: 'home', label: 'Home' },
  { value: 'schedule', label: 'Schedule' },
  { value: 'talent', label: 'Talent' },
  { value: 'events', label: 'Special events' },
  { value: 'hall', label: 'Hall & map' },
  { value: 'quests', label: 'Quests' },
  { value: 'me', label: 'Me' },
  { value: 'search', label: 'Search' },
  { value: 'journey', label: 'Build your day' },
  { value: 'tickets', label: 'Admission' },
  { value: 'shop', label: 'Shop' },
  { value: 'messages', label: 'Inbox' },
];

const nav = (key: string, label: string, extra: Partial<PropField> = {}): PropField => ({
  key, label, kind: 'nav', ...extra,
});

export const WIDGETS: Record<WidgetType, WidgetDef> = {
  // ── Today ────────────────────────────────────────────────────────────────
  dayPicker: {
    type: 'dayPicker', label: 'Day picker', category: 'Today',
    description: 'Day pills that drive every date-aware widget on the page.',
    fields: [], defaults: {},
  },
  upNext: {
    type: 'upNext', label: 'Up next', category: 'Today',
    description: 'A hero card for the next session, with shortcuts to the schedule and the room.',
    fields: [
      { key: 'showOpenSchedule', label: 'Show "Open schedule"', kind: 'toggle' },
      { key: 'showFindRoom', label: 'Show "Find the room"', kind: 'toggle' },
    ],
    defaults: { showOpenSchedule: true, showFindRoom: true },
  },
  promoRotator: {
    type: 'promoRotator', label: 'Promo rotator', category: 'Today',
    description: 'A rotating banner. Artwork and destinations come from the content API.',
    fields: [
      { key: 'label', label: 'Section label', kind: 'text', maxLength: 40, placeholder: 'Uses the content default' },
    ],
    defaults: { label: '' },
  },
  quickLinks: {
    type: 'quickLinks', label: 'Quick links', category: 'Today',
    description: 'A grid of shortcut tiles, each with a wireframe motif.',
    fields: [
      { key: 'useContent', label: 'Use tiles from the content API', kind: 'toggle',
        help: 'On: labels and counts stay live. Off: define your own below.' },
      { key: 'items', label: 'Tiles', kind: 'list', minItems: 1, maxItems: 8, itemLabel: 'Tile',
        showWhen: { key: 'useContent', equals: false },
        item: [
          { key: 'label', label: 'Label', kind: 'text', required: true, maxLength: 24 },
          { key: 'note', label: 'Note', kind: 'text', maxLength: 32 },
          nav('target', 'Opens'),
          { key: 'art', label: 'Motif', kind: 'select', options: ART_OPTIONS },
        ] },
    ],
    defaults: { useContent: true, items: [] },
  },
  journeyCta: {
    type: 'journeyCta', label: 'Build-your-day prompt', category: 'Today', requires: 'journey',
    description: 'Prompts the attendee to plan their day; collapses to a summary once they have.',
    fields: [
      { key: 'title', label: 'Title', kind: 'text', maxLength: 40, placeholder: 'Uses the content default' },
      { key: 'body', label: 'Body', kind: 'textarea', maxLength: 160, placeholder: 'Uses the content default' },
      { key: 'action', label: 'Button', kind: 'text', maxLength: 24, placeholder: 'Uses the content default' },
    ],
    defaults: { title: '', body: '', action: '' },
  },
  callSheet: {
    type: 'callSheet', label: 'Call Sheet', category: 'Today', requires: 'callSheet', behavioural: true,
    description: 'An AI-planned running order for the day, built from the attendee\u2019s interests.',
    fields: [
      { key: 'title', label: 'Title', kind: 'text', maxLength: 40, placeholder: 'Uses the content default' },
    ],
    defaults: { title: '' },
  },
  featuredTalent: {
    type: 'featuredTalent', label: 'Featured talent', category: 'Today', behavioural: true,
    description: 'A carousel of guests, matched to the attendee\u2019s interests.',
    fields: [
      { key: 'title', label: 'Title', kind: 'text', maxLength: 40 },
      { key: 'mode', label: 'Show', kind: 'select',
        options: [{ value: 'interests', label: 'Matches for the attendee' }, { value: 'all', label: 'Everyone' }] },
      { key: 'limit', label: 'How many', kind: 'number', min: 2, max: 10 },
    ],
    defaults: { title: 'Picked for you', mode: 'interests', limit: 5 },
  },
  specialEvents: {
    type: 'specialEvents', label: 'Special events', category: 'Today',
    description: 'Ticketed and free extras happening on the selected day.',
    fields: [
      { key: 'title', label: 'Title', kind: 'text', maxLength: 40 },
      { key: 'limit', label: 'How many', kind: 'number', min: 1, max: 8 },
    ],
    defaults: { title: 'Special events today', limit: 4 },
  },
  countdown: {
    type: 'countdown', label: 'Countdown', category: 'Today',
    description: 'Counts down to a moment \u2014 doors, a headline panel, the runway.',
    fields: [
      { key: 'label', label: 'Label', kind: 'text', required: true, maxLength: 40 },
      { key: 'target', label: 'Counts down to', kind: 'datetime', required: true },
      { key: 'doneLabel', label: 'When it arrives', kind: 'text', maxLength: 40 },
      { key: 'showSeconds', label: 'Show seconds', kind: 'toggle' },
    ],
    defaults: { label: 'Doors open in', target: '2026-09-17T16:00', doneLabel: 'The doors are open', showSeconds: true },
  },

  // ── Schedule ─────────────────────────────────────────────────────────────
  sessionList: {
    type: 'sessionList', label: 'Session list', category: 'Schedule',
    description: 'A filtered list of sessions \u2014 a track, a day, whatever is coming up.',
    fields: [
      { key: 'title', label: 'Title', kind: 'text', maxLength: 40 },
      { key: 'track', label: 'Track', kind: 'track' },
      { key: 'day', label: 'Day', kind: 'day' },
      { key: 'limit', label: 'How many', kind: 'number', min: 1, max: 10 },
      { key: 'showStar', label: 'Show star button', kind: 'toggle' },
    ],
    defaults: { title: 'Coming up', track: 'All', day: 'selected', limit: 4, showStar: true },
  },
  smartSuggestions: {
    type: 'smartSuggestions', label: 'Smart suggestions', category: 'Schedule', behavioural: true,
    description: 'Sessions suggested from what the attendee has starred, viewed and is booking. Computed on the device \u2014 no model call.',
    fields: [
      { key: 'title', label: 'Title', kind: 'text', maxLength: 40 },
      { key: 'strategy', label: 'Based on', kind: 'select',
        options: [
          { value: 'both', label: 'Behaviour and interests' },
          { value: 'behavior', label: 'Behaviour only' },
          { value: 'interests', label: 'Interests only' },
        ] },
      { key: 'limit', label: 'How many', kind: 'number', min: 1, max: 6 },
      { key: 'showReasons', label: 'Explain each suggestion', kind: 'toggle',
        help: 'Showing why builds trust; "Because you starred\u2026" reads as helpful, not creepy.' },
    ],
    defaults: { title: 'Suggested for you', strategy: 'both', limit: 3, showReasons: true },
  },

  // ── Engage ───────────────────────────────────────────────────────────────
  poll: {
    type: 'poll', label: 'Poll', category: 'Engage',
    description: 'A single-choice poll. Votes are collected per device; results update live.',
    fields: [
      { key: 'question', label: 'Question', kind: 'text', required: true, maxLength: 90 },
      { key: 'options', label: 'Options', kind: 'list', item: 'text', minItems: 2, maxItems: 6, itemLabel: 'Option' },
      { key: 'results', label: 'Show results', kind: 'select',
        options: [
          { value: 'afterVote', label: 'After the attendee votes' },
          { value: 'always', label: 'Always' },
          { value: 'never', label: 'Never (collect only)' },
        ] },
    ],
    defaults: {
      question: 'Which session are you most excited for?',
      options: ['Second Breakfast with the Halflings', 'Midnight Cosplay Runway', 'Charity auction'],
      results: 'afterVote',
    },
  },
  liveFeedback: {
    type: 'liveFeedback', label: 'Live feedback', category: 'Engage',
    description: 'A one-tap rating for the event or a session, with an optional comment.',
    fields: [
      { key: 'prompt', label: 'Prompt', kind: 'text', required: true, maxLength: 90 },
      { key: 'style', label: 'Style', kind: 'select',
        options: [
          { value: 'stars', label: 'Five stars' },
          { value: 'emoji', label: 'Five faces' },
          { value: 'thumbs', label: 'Thumbs up / down' },
        ] },
      { key: 'target', label: 'About', kind: 'select',
        options: [{ value: 'event', label: 'The event as a whole' }, { value: 'session', label: 'A specific session' }] },
      { key: 'sessionId', label: 'Session', kind: 'session', showWhen: { key: 'target', equals: 'session' } },
      { key: 'allowComment', label: 'Allow a comment', kind: 'toggle' },
      { key: 'thanks', label: 'Thank-you message', kind: 'text', maxLength: 120 },
    ],
    defaults: {
      prompt: 'How is the weekend going?', style: 'emoji', target: 'event', sessionId: '',
      allowComment: true, thanks: 'Thanks \u2014 that helps us tune the rest of the weekend.',
    },
  },
  quizCard: {
    type: 'quizCard', label: 'Quiz', category: 'Engage',
    description: 'A quick multiple-choice question that awards quest points, once per question.',
    fields: [
      { key: 'title', label: 'Title', kind: 'text', maxLength: 40 },
      { key: 'source', label: 'Questions from', kind: 'select',
        options: [{ value: 'content', label: 'The content API' }, { value: 'custom', label: 'Write my own' }] },
      { key: 'start', label: 'Start at question', kind: 'number', min: 0, max: 50, showWhen: { key: 'source', equals: 'content' } },
      { key: 'count', label: 'How many', kind: 'number', min: 1, max: 5, showWhen: { key: 'source', equals: 'content' } },
      { key: 'points', label: 'Points per correct answer', kind: 'number', min: 0, max: 500 },
      { key: 'questions', label: 'Questions', kind: 'list', minItems: 1, maxItems: 5, itemLabel: 'Question',
        showWhen: { key: 'source', equals: 'custom' },
        item: [
          { key: 'q', label: 'Question', kind: 'text', required: true, maxLength: 120 },
          { key: 'a', label: 'Answers', kind: 'list', item: 'text', minItems: 2, maxItems: 4, itemLabel: 'Answer' },
          { key: 'correct', label: 'Correct answer (1 = first)', kind: 'number', min: 1, max: 4 },
        ] },
    ],
    defaults: { title: 'Quick quiz', source: 'content', start: 0, count: 1, points: 50, questions: [] },
  },

  // ── Commerce ─────────────────────────────────────────────────────────────
  ticketCta: {
    type: 'ticketCta', label: 'Admission prompt', category: 'Commerce', requires: 'tickets',
    description: 'Prompts the attendee to buy admission.',
    fields: [
      { key: 'title', label: 'Title', kind: 'text', required: true, maxLength: 40 },
      { key: 'body', label: 'Body', kind: 'textarea', maxLength: 160 },
      { key: 'action', label: 'Button', kind: 'text', required: true, maxLength: 24 },
      { key: 'hideWhenBadge', label: 'Hide once they hold a badge', kind: 'toggle' },
    ],
    defaults: {
      title: 'Get your badge', body: 'Buy admission here and your badge is issued instantly, ready to scan.',
      action: 'Buy admission', hideWhenBadge: true,
    },
  },
  merchStrip: {
    type: 'merchStrip', label: 'Merch strip', category: 'Commerce', requires: 'shop',
    description: 'A shelf of merchandise from the content API.',
    fields: [
      { key: 'title', label: 'Title', kind: 'text', maxLength: 40 },
      { key: 'limit', label: 'How many', kind: 'number', min: 2, max: 8 },
    ],
    defaults: { title: 'Official merch', limit: 4 },
  },

  // ── Content ──────────────────────────────────────────────────────────────
  banner: {
    type: 'banner', label: 'Announcement banner', category: 'Content',
    description: 'A message strip \u2014 a schedule change, a sponsor, a welcome.',
    fields: [
      { key: 'tone', label: 'Tone', kind: 'select',
        options: [{ value: 'info', label: 'Info' }, { value: 'highlight', label: 'Highlight' }, { value: 'alert', label: 'Alert' }] },
      { key: 'title', label: 'Title', kind: 'text', required: true, maxLength: 60 },
      { key: 'body', label: 'Body', kind: 'textarea', maxLength: 200 },
      { key: 'actionLabel', label: 'Link label', kind: 'text', maxLength: 24 },
      nav('target', 'Links to', { help: 'Leave as "No link" for a plain message.' }),
      { key: 'dismissible', label: 'Attendee can dismiss', kind: 'toggle' },
    ],
    defaults: { tone: 'highlight', title: 'Welcome to the weekend', body: 'Doors open at 09:00. Photo op studios open at 10:00.', actionLabel: '', target: null, dismissible: true },
  },
  richText: {
    type: 'richText', label: 'Text block', category: 'Content',
    description: 'A heading and a paragraph.',
    fields: [
      { key: 'heading', label: 'Heading', kind: 'text', maxLength: 60 },
      { key: 'body', label: 'Body', kind: 'textarea', maxLength: 400 },
      { key: 'align', label: 'Alignment', kind: 'select',
        options: [{ value: 'left', label: 'Left' }, { value: 'center', label: 'Centre' }] },
    ],
    defaults: { heading: 'Good to know', body: 'Add practical information here.', align: 'left' },
  },
  ctaButton: {
    type: 'ctaButton', label: 'Button', category: 'Content',
    description: 'A single call to action linking anywhere in the app.',
    fields: [
      { key: 'label', label: 'Label', kind: 'text', required: true, maxLength: 32 },
      { key: 'note', label: 'Small print', kind: 'text', maxLength: 60 },
      nav('target', 'Opens'),
      { key: 'variant', label: 'Style', kind: 'select',
        options: [{ value: 'primary', label: 'Primary' }, { value: 'secondary', label: 'Secondary' }] },
    ],
    defaults: { label: 'See the full schedule', note: '', target: { screen: 'schedule' }, variant: 'primary' },
  },
  spacer: {
    type: 'spacer', label: 'Spacer', category: 'Content',
    description: 'Vertical space, optionally with a fading rule.',
    fields: [
      { key: 'height', label: 'Height', kind: 'number', min: 4, max: 64 },
      { key: 'line', label: 'Draw a rule', kind: 'toggle' },
    ],
    defaults: { height: 16, line: false },
  },
};

export const WIDGET_TYPES = Object.keys(WIDGETS) as WidgetType[];

export const WIDGET_CATEGORIES: WidgetCategory[] = ['Today', 'Schedule', 'Engage', 'Commerce', 'Content'];

export function isWidgetType(v: unknown): v is WidgetType {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(WIDGETS, v);
}

let counter = 0;

/** A short, unique-enough id. Persisted, so it only needs to be stable. */
export function newWidgetId(type: string): string {
  counter += 1;
  const rand = Math.random().toString(36).slice(2, 6);
  return `${type}-${Date.now().toString(36).slice(-4)}${rand}${counter}`;
}

/** A fresh instance with its own copy of the defaults. */
export function makeWidget(type: WidgetType, id: string = newWidgetId(type)): WidgetInstance {
  return { id, type, props: JSON.parse(JSON.stringify(WIDGETS[type].defaults)) };
}
